import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Grid,
  TextField,
  ButtonGroup,
  Button,
  Autocomplete,
  FormControl,
  FormLabel,
  Typography,
  Paper,
  Fade,
  Checkbox,
  FormControlLabel,
  IconButton,
  LinearProgress,
  Tooltip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import Swal from "sweetalert2";
import {
  CommonSubHeader,
  useLocationHelpers,
  NavButton,
} from "../../Components/helpers/Helpers";
import api from "../../api/axios";
import { useSearchParams } from "react-router-dom";

import { DndContext, closestCenter } from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { DragOverlay } from "@dnd-kit/core";
import { CustomButtonPlusIcon } from "../../Components/Common/Design/CustomButtonPlusIcon";
import LoadingPage from "../../Components/Common/Loading/LoadingPage";
import { Save } from "lucide-react";

/* ========================= 
   SECCIONES BASE
========================= */
// key †’ identificador lógico
// title †’ solo UI
// id †’ drag & drop / react key
const initialSections = [
  { id: "firma", key: "firma", title: "Firma", fixed: true },
  {
    id: "documentacion",
    key: "documentacion",
    title: "Documentación",
    fixed: true,
  },
  { id: "revision", key: "revision", title: "Revisión", fixed: true },
  {
    id: "formulario-fi",
    key: "formulario-fi",
    title: "Formulario FI",
    fixed: true,
  },
];

/* =========================
   ITEM DRAGGABLE
========================= */
const SortableSection = ({ section, index, onRemove }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    borderRadius: 10,
    background: "#ffffff",
    boxShadow: isDragging
      ? "0 8px 18px rgba(15, 23, 42, 0.10)"
      : "0 2px 6px rgba(15, 23, 42, 0.06)",
    opacity: isDragging ? 0 : 1,
    display: "flex",
    justifyContent: "space-between",
    cursor: isDragging ? "grabbing" : "grab",
    width: "100%",
    userSelect: "none",
    WebkitUserSelect: "none",
    touchAction: "none",
  };

  return (
    <div ref={setNodeRef} style={style}>
      <Tooltip
        title={section?.title || ""}
        placement="top"
        arrow
        disableHoverListener={!(section?.title?.length > 40)}
      >
        <Box
          display="flex"
          alignItems="center"
          gap={1}
          padding={"10px 15px"}
          width={"100%"}
          overflow="hidden"
          sx={{
            cursor: isDragging ? "grabbing" : "grab",
            userSelect: "none",
            WebkitUserSelect: "none",
            touchAction: "none",
            caretColor: "transparent",
          }}
          {...attributes}
          {...listeners}
        >
          <Typography
            fontWeight={600}
            color="#0d47a1"
            sx={{
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
              overflow: "hidden",
              maxWidth: "100%",
              cursor: section?.title?.length > 200 ? "pointer" : "default",
            }}
          >
            {index + 1} .{" "}
            {section?.title?.length > 200
              ? section.title.slice(0, 200) + "..."
              : section.title}
          </Typography>
        </Box>
      </Tooltip>
      <IconButton
        size="small"
        sx={{
          color: "#d32f2f",
          "&:hover": { backgroundColor: "rgba(211,47,47,0.1)" },
        }}
        onClick={(e) => {
          e.stopPropagation();
          onRemove(section);
        }}
      >
        <DeleteIcon fontSize="small" />
      </IconButton>
    </div>
  );
};

/* =========================
   MAIN
========================= */
const Editar = () => {
  const { goPanel, goTable, buildTableUrl } = useLocationHelpers();

  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");

  const [activeTab, setActiveTab] = useState("basic");
  const [companies, setCompanies] = useState([]);
  const [sections, setSections] = useState(initialSections);
  const [workflow, setWorkflow] = useState([]);

  const [newSectionName, setNewSectionName] = useState("");
  const [activeId, setActiveId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [workflowProgress, setWorkflowProgress] = useState(null);
  const [progressIntervalId, setProgressIntervalId] = useState(null);
  const workflowPollRef = useRef(null);
  const workflowPollInFlightRef = useRef(false);
  const workflowQueueTimeoutRef = useRef(null);

  const clearWorkflowPolling = useCallback(() => {
    if (workflowPollRef.current) {
      clearTimeout(workflowPollRef.current);
      workflowPollRef.current = null;
    }

    if (workflowQueueTimeoutRef.current) {
      clearTimeout(workflowQueueTimeoutRef.current);
      workflowQueueTimeoutRef.current = null;
    }

    if (progressIntervalId) {
      clearInterval(progressIntervalId);
      setProgressIntervalId(null);
    }
  }, [progressIntervalId]);

  const [caseTypeData, setCaseTypeData] = useState({
    company: null,
    case_type: "",
    case_form: "",
  });

  const tabs = [
    { id: "basic", label: "Información básica" },
    { id: "workflow", label: "Flujo de trabajo" },
  ];

  useEffect(() => {
    setWorkflowProgress(null);
    clearWorkflowPolling();
  }, [id, clearWorkflowPolling]);

  useEffect(() => {
    const getData = async () => {
      setIsLoading(true);

      try {
        const { data } = await api.post(
          `${process.env.REACT_APP_API_URL}/casestypes/edit`,
          { id: id },
        );

        if (!data) return;

        const { case_type } = data;

        // Poblamos empresa seleccionada
        const company = case_type.company_relation
          ? {
              id: case_type.company_relation.id,
              company_name: case_type.company_relation.company_name,
            }
          : null;

        const companiesWithDefault = [
          { id: null, company_name: "Seleccionar" },
          ...data?.companies_list,
        ];
        setCompanies(companiesWithDefault || []);

        setCaseTypeData({
          company,
          case_type: case_type?.case_type || "",
          case_form: case_type?.case_form || "",
        });

        if (!data?.workflow) return;

        // Secciones base seleccionadas
        const selectedSections = initialSections.map((s) => ({
          ...s,
          selected: data.workflow.some((w) => w.key === s.key),
        }));

        // Workflow personalizado (no fijo)
        const customWorkflow = data.workflow
          .filter((w) => !initialSections.some((s) => s.key === w.key))
          .map((w) => ({
            id: `custom-${w.id}`,
            key: w.key,
            title: w.title,
            fixed: false,
            order: w.order,
          }))
          .sort((a, b) => a.order - b.order);

        setSections(selectedSections);

        setWorkflow([
          ...selectedSections.filter((s) => s.selected),
          ...customWorkflow,
        ]);
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    };

    getData();
  }, [id]);

  useEffect(() => {
    return () => {
      clearWorkflowPolling();
    };
  }, [clearWorkflowPolling]);

  /* =========================
     FUNCIONES
  ========================== */
  const handleFieldChange = (name, value) => {
    setCaseTypeData((prev) => ({ ...prev, [name]: value }));
  };

  const toggleMainSection = (sectionId) => {
    setSections((prevSections) => {
      const current = prevSections.find((s) => s.id === sectionId);
      if (!current) return prevSections;

      const willSelect = !current.selected;

      setWorkflow((prevWorkflow) =>
        willSelect
          ? [...prevWorkflow, current]
          : prevWorkflow.filter((w) => w.id !== sectionId),
      );

      return prevSections.map((s) =>
        s.id === sectionId ? { ...s, selected: willSelect } : s,
      );
    });
  };

  const addCustomSection = () => {
    if (!newSectionName.trim()) return;
    const normalizedKey = newSectionName
      .toLocaleLowerCase()
      .trim()
      .replace(/\s+/g, "-");

    const newSection = {
      id: normalizedKey,
      key: normalizedKey,
      title: newSectionName,
      fixed: false,
    };
    setWorkflow((prev) => [...prev, newSection]);
    setNewSectionName("");
  };

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    setWorkflow((prev) => {
      const oldIndex = prev.findIndex((s) => s.id === active.id);
      const newIndex = prev.findIndex((s) => s.id === over.id);
      const copy = [...prev];
      const [moved] = copy.splice(oldIndex, 1);
      copy.splice(newIndex, 0, moved);
      return copy;
    });
  };

  const removeSection = (section) => {
    setWorkflow((prev) => prev.filter((s) => s.id !== section.id));
    setSections((prev) =>
      prev.map((s) => (s.id === section.id ? { ...s, selected: false } : s)),
    );
  };

  const getStatusActionBySection = (section) => {
    const rawValue = (section?.key || section?.id || section?.title || "")
      .toString()
      .toLocaleLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .trim();

    if (rawValue.includes("documentacion") || rawValue === "documento") {
      return "expedientes/docs";
    }

    if (rawValue.includes("revision")) {
      return "expedientes/review";
    }

    if (rawValue.includes("firma")) {
      return "expedientes/sign";
    }

    return "";
  };

  const handleBasicSave = async () => {
    if (!caseTypeData?.company?.id) {
      return Swal.fire({
        icon: "error",
        title: "Empresa requerida",
        text: "Selecciona una empresa antes de guardar la información básica.",
        confirmButtonColor: "#1ab394",
      });
    }

    if (!caseTypeData?.case_type?.trim()) {
      return Swal.fire({
        icon: "error",
        title: "Tipo de caso requerido",
        text: "Introduce el nombre del tipo de caso antes de guardar.",
        confirmButtonColor: "#1ab394",
      });
    }

    const confirm = await Swal.fire({
      icon: "question",
      title: "Confirmar actualización",
      text: "¿Deseas guardar la información básica de este tipo de caso?",
      showCancelButton: true,
      confirmButtonText: "Guardar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#1ab394",
      cancelButtonColor: "#aaa",
    });

    if (!confirm.isConfirmed) return;

    try {
      Swal.fire({
        title: "Guardando información básica...",
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => Swal.showLoading(),
      });

      await api.post(
        `${process.env.REACT_APP_API_URL}/casestypes/update-basic`,
        {
          id,
          company: caseTypeData.company,
          case_type: caseTypeData.case_type,
          case_form: caseTypeData.case_form,
        },
      );

      Swal.close();

      await Swal.fire({
        icon: "success",
        title: "Guardado",
        text: "La información básica se ha actualizado correctamente.",
        confirmButtonColor: "#1ab394",
      });
    } catch (error) {
      Swal.close();

      await Swal.fire({
        icon: "error",
        title: "No se pudo guardar",
        text:
          error.response?.data?.message ||
          "Ocurrió un error al guardar la información básica.",
        confirmButtonColor: "#d33",
      });
    }
  };

  const pollWorkflowProgress = (jobId) => {
    if (!jobId || !String(jobId).trim()) {
      setWorkflowProgress(null);
      return;
    }

    clearWorkflowPolling();

    workflowQueueTimeoutRef.current = setTimeout(async () => {
      clearWorkflowPolling();
      setWorkflowProgress(null);

      await Swal.fire({
        icon: "warning",
        title: "Actualización en cola",
        text: "La actualización del flujo quedó en espera porque no hubo respuesta del worker de Laravel en Docker. Revisa el proceso de cola y vuelve a intentarlo.",
        confirmButtonColor: "#f59e0b",
      });
    }, 20000);

    const pollOnce = async () => {
      if (workflowPollInFlightRef.current) return;
      workflowPollInFlightRef.current = true;

      try {
        const { data } = await api.get(
          `${process.env.REACT_APP_API_URL}/casestypes/workflow-progress/${jobId}`,
        );

        if (!data || typeof data !== "object") {
          setWorkflowProgress(null);
          return;
        }

        setWorkflowProgress(data);

        if (data?.status === "finished" || data?.status === "failed") {
          clearWorkflowPolling();
          setWorkflowProgress({
            ...(data || {}),
            progress: 100,
            message:
              data?.status === "finished"
                ? "Flujo actualizado correctamente"
                : data?.message || "Se produjo un error al procesar el flujo",
          });

          if (data?.status === "finished") {
            await Swal.fire({
              icon: "success",
              title: "Flujo actualizado",
              text: "El flujo de trabajo se ha guardado correctamente.",
              confirmButtonColor: "#1ab394",
            });
            setWorkflowProgress(null);
          }

          return;
        }

        workflowPollRef.current = setTimeout(pollOnce, 1000);
        setProgressIntervalId(workflowPollRef.current);
      } catch (error) {
        clearWorkflowPolling();
        setWorkflowProgress(null);
        await Swal.fire({
          icon: "error",
          title: "Error al guardar el flujo",
          text:
            error.response?.data?.message ||
            "Ocurrió un error al guardar el flujo de trabajo.",
          confirmButtonColor: "#d33",
        });
      } finally {
        workflowPollInFlightRef.current = false;
      }
    };

    workflowPollRef.current = setTimeout(pollOnce, 1000);
    setProgressIntervalId(workflowPollRef.current);
  };

  const handleWorkflowSave = async () => {
    if (!workflow.length) {
      return Swal.fire({
        icon: "error",
        title: "Flujo vacío",
        text: "Añade al menos una sección antes de guardar el flujo.",
        confirmButtonColor: "#1ab394",
      });
    }

    if (newSectionName.trim()) {
      return Swal.fire({
        icon: "warning",
        title: "Sección pendiente por agregar",
        confirmButtonColor: "#1ab394",
        html: '<p style="margin:0; line-height:1.5;">Hay texto escrito en <strong>Agregar sección</strong>. Pulsa <strong>Agregar</strong> antes de guardar el flujo.</p>',
      });
    }

    try {
      Swal.fire({
        title: "Calculando impacto...",
        text: "Revisando expedientes y tareas afectadas antes de guardar.",
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => Swal.showLoading(),
      });

      const payload = {
        id,
        workflow: workflow.map((s, i) => ({
          key: s.key || s.id || s.title,
          title: s.title,
          order: i + 1,
          status_action: getStatusActionBySection(s),
        })),
      };

      const { data } = await api.post(
        `${process.env.REACT_APP_API_URL}/casestypes/pre-validate-update`,
        payload,
      );

      Swal.close();

      const impactHtml = `
        <div style="text-align:left; line-height:1.6;">
          <p style="margin:0 0 8px;"><strong>Tipo de caso:</strong> ${data.caseType || "Sin nombre"}</p>
          <p style="margin:0 0 8px;"><strong>Expedientes afectados:</strong> ${data.affectedCases || 0}</p>
          <p style="margin:0 0 8px;"><strong>Tareas relacionadas:</strong> ${data.affectedTasks || 0}</p>
          <p style="margin:0 0 8px;"><strong>Pasos del flujo:</strong> ${data.workflowSteps || 0}</p>
          <p style="margin:0 0 8px;"><strong>Tiempo estimado:</strong> ${data.estimatedMinutes || 0} min aprox.</p>
          <p style="margin:0; color:#475569;">${data.message || "Se aplicarán los cambios en los expedientes relacionados."}</p>
        </div>
      `;

      const confirm = await Swal.fire({
        icon: "info",
        title: "Impacto estimado antes de guardar",
        html: impactHtml,
        showCancelButton: true,
        confirmButtonText: "Guardar flujo",
        cancelButtonText: "Cancelar",
        confirmButtonColor: "#1ab394",
        cancelButtonColor: "#aaa",
      });

      if (!confirm.isConfirmed) return;

      try {
        Swal.fire({
          title: "Procesando flujo...",
          html: "Preparando actualización masiva de expedientes.",
          allowOutsideClick: false,
          allowEscapeKey: false,
          didOpen: () => Swal.showLoading(),
        });

        const saveResponse = await api.post(
          `${process.env.REACT_APP_API_URL}/casestypes/update-workflow`,
          payload,
        );

        Swal.close();

        const jobId = saveResponse?.data?.job_id;

        if (!jobId) {
          setWorkflowProgress(null);
          await Swal.fire({
            icon: "success",
            title: "Flujo actualizado",
            text: "Los cambios del flujo se han aplicado correctamente.",
            confirmButtonColor: "#1ab394",
          });
          return;
        }

        setWorkflowProgress({
          status: "queued",
          progress: 0,
          processed: 0,
          total: 0,
          eta_ms: 0,
          message: "En cola para procesar cambios del flujo",
        });

        pollWorkflowProgress(jobId);
      } catch (saveError) {
        Swal.close();
        setWorkflowProgress(null);

        await Swal.fire({
          icon: "error",
          title: "Error al guardar el flujo",
          text:
            saveError.response?.data?.message ||
            "Ocurrió un error al guardar el flujo de trabajo.",
          confirmButtonColor: "#d33",
        });
      }
    } catch (error) {
      Swal.close();
      setWorkflowProgress(null);

      await Swal.fire({
        icon: "error",
        title: "Error al validar impacto",
        text:
          error.response?.data?.message ||
          "No se pudo calcular el impacto antes de guardar.",
        confirmButtonColor: "#d33",
      });
    }
  };

  const buttonStyles = {
    p: 2,
    mb: 2,
    borderRadius: 2,
    backgroundColor: "white",
  };
  const contentStyles = {
    p: 2,
    background: "white",
    my: "5px",
    borderRadius: 2,
  };
  const stylesContainer = {
    p: 2,
    mb: 2,
    borderRadius: 2,
    backgroundColor: "#f5f9ff",
  };

  if (isLoading) {
    return (
      <>
        <CommonSubHeader
          title="Tipos de casos"
          subTitle="Editar"
          specialRute="tiposdecasos"
        />
        <Box p={3}>
          <LoadingPage isLoading={isLoading} heightProp="380px" />
        </Box>
      </>
    );
  }

  return (
    <>
      <CommonSubHeader
        title="Tipos de casos"
        subTitle="Editar"
        specialRute="tiposdecasos"
      />
      <Box p={3}>
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            gap={1}
          >
            <Box display="flex" gap={1} sx={{ flexWrap: "wrap" }}>
              {tabs.map((tab) => (
                <Button
                  key={tab.id}
                  variant={activeTab === tab.id ? "contained" : "outlined"}
                  color={activeTab === tab.id ? "primary" : "inherit"}
                  onClick={() => setActiveTab(tab.id)}
                  sx={{
                    minWidth: 180,
                    textTransform: "none",
                    borderRadius: 2,
                    fontWeight: 600,
                  }}
                >
                  {tab.label}
                </Button>
              ))}
            </Box>

            {activeTab === "basic" ? (
              <CustomButtonPlusIcon
                icon={Save}
                text={"Guardar básica"}
                size="small"
                position={"start"}
                color={"success"}
                variant="contained"
                handleFunction={handleBasicSave}
              />
            ) : (
              <CustomButtonPlusIcon
                icon={Save}
                text={"Guardar flujo"}
                size="small"
                position={"start"}
                color={"success"}
                variant="contained"
                handleFunction={handleWorkflowSave}
              />
            )}
          </Box>
        </Paper>

        {activeTab === "basic" && (
          <Fade in timeout={800}>
            <Paper
              elevation={2}
              sx={{ p: 2, borderRadius: 2, backgroundColor: "#f5f9ff" }}
            >
              <Grid container sx={contentStyles}>
                {/* Empresa */}
                <Grid item xs={12} sx={{ mb: 2 }}>
                  <Autocomplete
                    size="small"
                    options={companies}
                    value={caseTypeData?.company}
                    getOptionLabel={(o) => o.company_name || ""}
                    onChange={(_, v) => handleFieldChange("company", v)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Empresa"
                        variant="outlined"
                        sx={{ background: "#fff" }}
                      />
                    )}
                  />
                </Grid>
                {/* Tipo de caso */}
                <Grid item xs={12} sx={{ mb: 2 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Tipo de caso"
                    variant="outlined"
                    value={caseTypeData?.case_type}
                    onChange={(e) =>
                      handleFieldChange("case_type", e.target.value)
                    }
                  />
                </Grid>
                {/* Formulario */}
                <Grid item xs={12} sx={{ mb: 2 }}>
                  <FormControl fullWidth>
                    <FormLabel sx={{ mb: 1 }}>Formulario</FormLabel>
                    <textarea
                      style={{
                        height: "100px",
                        resize: "none",
                        padding: "6px 13px",
                        fontSize: "15px",
                        borderRadius: "10px",
                        border: "1px solid rgba(128, 128, 128, 0.35)",
                      }}
                      onChange={(e) =>
                        handleFieldChange("case_form", e.target.value)
                      }
                      value={caseTypeData?.case_form}
                    />
                  </FormControl>
                </Grid>
              </Grid>
            </Paper>
          </Fade>
        )}

        {activeTab === "workflow" && (
          <Fade in timeout={800}>
            <Box>
              {/* SECCIONES PRINCIPALES */}
              <Paper elevation={2} sx={stylesContainer}>
                <Typography variant="h6" color="#0d47a1" fontWeight={600}>
                  Secciones principales
                </Typography>
                <Box display="flex" flexDirection="row" gap={1} mt={2}>
                  {sections.map((s, i) => (
                    <FormControlLabel
                      key={s.id}
                      control={
                        <Checkbox
                          checked={s.selected}
                          onChange={() => toggleMainSection(s.id)}
                          sx={{
                            color: "#1565c0",
                            "&.Mui-checked": { color: "#0d47a1" },
                          }}
                        />
                      }
                      label={`${i + 1}. ${s.title}`}
                    />
                  ))}
                </Box>
              </Paper>

              {/* AGREGAR SECCIÓN */}
              <Paper elevation={2} sx={stylesContainer}>
                <Typography variant="h6" color="#0d47a1" fontWeight={600}>
                  Agregar sección
                </Typography>
                <Box display="flex" gap={2} mt={2}>
                  <TextField
                    size="small"
                    label="Nombre sección"
                    variant="outlined"
                    value={newSectionName}
                    onChange={(e) => setNewSectionName(e.target.value)}
                    sx={{ flex: 1, background: "#fff" }}
                  />
                  <Button
                    variant="contained"
                    sx={{
                      backgroundColor: "#1565c0",
                      "&:hover": { background: "#0d47a1" },
                    }}
                    onClick={addCustomSection}
                  >
                    Agregar
                  </Button>
                </Box>
              </Paper>

              {workflowProgress && (
                <Paper elevation={2} sx={{ ...stylesContainer, p: 3 }}>
                  <Typography
                    variant="subtitle1"
                    color="#0d47a1"
                    fontWeight={700}
                  >
                    Progreso de actualización
                  </Typography>
                  <Typography variant="body2" color="#475569" sx={{ mt: 1 }}>
                    {workflowProgress.message || "Procesando..."}
                  </Typography>

                  <Box sx={{ mt: 2 }}>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(
                        100,
                        Number(workflowProgress.progress || 0),
                      )}
                      sx={{
                        height: 10,
                        borderRadius: 999,
                        backgroundColor: "#dbeafe",
                      }}
                    />
                  </Box>

                  <Box display="flex" justifyContent="space-between" mt={1.5}>
                    <Typography variant="caption" color="#334155">
                      {Math.min(100, Number(workflowProgress.progress || 0))}%
                    </Typography>
                    <Typography variant="caption" color="#334155">
                      ETA:{" "}
                      {workflowProgress.eta_ms
                        ? `${Math.max(0, Math.ceil(workflowProgress.eta_ms / 1000))}s`
                        : "calculando..."}
                    </Typography>
                  </Box>
                </Paper>
              )}

              {/* FLUJO DE TRABAJO */}
              <Paper elevation={2} sx={stylesContainer}>
                <Typography variant="h6" color="#0d47a1" fontWeight={600}>
                  Flujo de trabajo en expediente
                </Typography>

                <DndContext
                  collisionDetection={closestCenter}
                  onDragStart={(event) => setActiveId(event.active.id)}
                  onDragEnd={(event) => {
                    handleDragEnd(event);
                    setActiveId(null);
                  }}
                  onDragCancel={() => setActiveId(null)}
                >
                  <SortableContext
                    items={workflow.map((w) => w.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <Box display="grid" gap={2} mt={2}>
                      {workflow.map((section, index) => (
                        <SortableSection
                          key={section.id}
                          section={section}
                          index={index}
                          onRemove={removeSection}
                        />
                      ))}
                      <DragOverlay adjustScale={false}>
                        {activeId ? (
                          <Paper
                            sx={{
                              p: "10px 15px",
                              borderRadius: 2,
                              background: "#fff",
                              boxShadow: "0 10px 22px rgba(15, 23, 42, 0.12)",
                              cursor: "grabbing",
                              userSelect: "none",
                              WebkitUserSelect: "none",
                            }}
                          >
                            <Typography fontWeight={600} color="#0d47a1">
                              {workflow.find((s) => s.id === activeId)?.title}
                            </Typography>
                          </Paper>
                        ) : null}
                      </DragOverlay>
                    </Box>
                  </SortableContext>
                </DndContext>
              </Paper>
            </Box>
          </Fade>
        )}

        {/* BOTONES INFERIORES */}
        <Box sx={{ mt: "10px" }}>
          <Paper elevation={2} sx={buttonStyles}>
            <ButtonGroup
              fullWidth
              variant="outlined"
              sx={{ background: "white" }}
            >
              <NavButton
                color="error"
                variant="outlined"
                to={buildTableUrl("tiposdecasos")}
                onClick={() => goTable("tiposdecasos", "Tipos de casos")}
              >
                Tabla
              </NavButton>
              <Button onClick={goPanel} variant="outlined">
                Inicio
              </Button>
            </ButtonGroup>
          </Paper>
        </Box>
      </Box>
    </>
  );
};

export default Editar;
